import { useState } from 'react';
import { Motif, Wordmark, currentTheme } from '../brand/Brand';
import { Icon } from '../icons';

// /mcp/authorize and /legal/accept send signed-out people here with ?return_to=;
// the server validates it and redirects there after sign-in (P1-41).
function returnToParam(): string | null {
  return new URLSearchParams(window.location.search).get('return_to');
}

function providerHref(provider: string, returnTo: string | null): string {
  return returnTo ? `/auth/${provider}?return_to=${encodeURIComponent(returnTo)}` : `/auth/${provider}`;
}

export function LoginPage() {
  const [returnTo] = useState(returnToParam);
  const [email, setEmail] = useState('');
  const [magicSent, setMagicSent] = useState(false);
  const [magicError, setMagicError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);

  const handleMagicLink = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    setSending(true);
    setMagicError(null);

    try {
      const res = await fetch('/auth/magic', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(returnTo ? { email: email.trim(), return_to: returnTo } : { email: email.trim() }),
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({ error: 'Failed to send' }));
        throw new Error((body as { error?: string }).error ?? 'Failed to send magic link');
      }

      setMagicSent(true);
    } catch (err) {
      setMagicError((err as Error).message);
    } finally {
      setSending(false);
    }
  };

  const theme = currentTheme();

  return (
    <div className="fr">
      <div className="fr-art">
        <Motif width={720} height={900} theme={theme} seed={23} pitch={28} fx={0.55} fy={0.5} mask="right-soft" id="signin" />
      </div>
      <main className="fr-main">
        <div className="fr-card">
          <Wordmark size={22} href="/" />
          {magicSent ? (
            <>
              <span className="fr-sent-icon"><Icon name="email" size={22} /></span>
              <div className="fr-head">
                <h1>Check your email</h1>
                <p>We sent a sign-in link to <strong>{email}</strong>. It works once, within 15 minutes.</p>
              </div>
              <p className="fr-quiet">Nothing after a minute? Check spam, or</p>
              <div className="fr-row">
                <button className="fr-btn" onClick={(e) => { void handleMagicLink(e as unknown as React.FormEvent); }} disabled={sending}>
                  {sending ? 'Sending…' : 'Send it again'}
                </button>
                <button className="fr-btn" onClick={() => { setMagicSent(false); setEmail(''); }}>Use a different email</button>
              </div>
              {magicError && <p className="fr-error" role="alert">{magicError}</p>}
            </>
          ) : (
            <>
              <div className="fr-head">
                <h1>Sign in</h1>
                <p>New here? The same buttons create your account.</p>
              </div>
              <div className="fr-providers">
          <a href={providerHref('github', returnTo)} className="fr-btn fr-oauth">
            <svg width="17" height="17" aria-hidden="true" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0024 12c0-6.63-5.37-12-12-12z" />
            </svg>
            Continue with GitHub
          </a>

          <a href={providerHref('google', returnTo)} className="fr-btn fr-oauth">
            <svg width="17" height="17" aria-hidden="true" viewBox="0 0 24 24">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            Continue with Google
          </a>

          <a href={providerHref('apple', returnTo)} className="fr-btn fr-oauth">
            <svg width="17" height="17" aria-hidden="true" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
            </svg>
            Continue with Apple
          </a>
              </div>
              <div className="fr-or"><span>or with email</span></div>
              <form className="fr-form" onSubmit={handleMagicLink}>
                <label className="fr-label">
                  Email
                  <input
                    type="email"
                    className="fr-input"
                    placeholder="you@example.com"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </label>
                <button type="submit" className="fr-btn fr-btn-primary" disabled={sending}>
                  {sending ? 'Sending…' : 'Email me a sign-in link'}
                </button>
                {magicError && <p className="fr-error" role="alert">{magicError}</p>}
              </form>
              <p className="fr-quiet">New accounts start with a free Pro trial. No card needed.</p>
            </>
          )}
          <p className="fr-legal">
            By signing in you agree to the{' '}
            <a href="https://neuralconfig.com/terms/">Terms</a> and{' '}
            <a href="https://neuralconfig.com/privacy/neuralrepo/">Privacy Policy</a>.
          </p>
        </div>
      </main>
    </div>
  );
}
